/**
 * DRBFM Processing Step Component
 *
 * Displays real-time progress of DRBFM analysis:
 * - Overall progress bar
 * - Individual flow status indicators
 * - Progress messages
 * - Cancel button
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  Loop as ProcessingIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  CompareArrows as Flow1Icon,
  Search as Flow2Icon,
  Warning as Flow3Icon,
  Build as Flow4Icon,
  TableChart as Flow5Icon,
} from '@mui/icons-material';
import { DRBFMAnalysisProgress, FlowProgress } from '../../../types';

interface DRBFMProcessingStepProps {
  progress: DRBFMAnalysisProgress;
  onCancel: () => void;
}

// Flow configuration
interface FlowConfig {
  id: number;
  key: keyof FlowProgress;
  icon: React.ReactNode;
  labelKey: string;
  descriptionKey: string;
}

const flowConfigs: FlowConfig[] = [
  {
    id: 1,
    key: 'flow1',
    icon: <Flow1Icon />,
    labelKey: 'drbfm.processing.flow1.title',
    descriptionKey: 'drbfm.processing.flow1.description',
  },
  {
    id: 2,
    key: 'flow2',
    icon: <Flow2Icon />,
    labelKey: 'drbfm.processing.flow2.title',
    descriptionKey: 'drbfm.processing.flow2.description',
  },
  {
    id: 3,
    key: 'flow3',
    icon: <Flow3Icon />,
    labelKey: 'drbfm.processing.flow3.title',
    descriptionKey: 'drbfm.processing.flow3.description',
  },
  {
    id: 4,
    key: 'flow4',
    icon: <Flow4Icon />,
    labelKey: 'drbfm.processing.flow4.title',
    descriptionKey: 'drbfm.processing.flow4.description',
  },
  {
    id: 5,
    key: 'flow5',
    icon: <Flow5Icon />,
    labelKey: 'drbfm.processing.flow5.title',
    descriptionKey: 'drbfm.processing.flow5.description',
  },
];

const DRBFMProcessingStep: React.FC<DRBFMProcessingStepProps> = ({
  progress,
  onCancel,
}) => {
  const { t } = useTranslation();

  // Get status icon for a flow
  const getStatusIcon = (status: FlowProgress[keyof FlowProgress] | undefined) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'in_progress':
        return <ProcessingIcon color="primary" sx={{ animation: 'spin 1s linear infinite' }} />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  // Get status chip for a flow
  const getStatusChip = (status: FlowProgress[keyof FlowProgress] | undefined) => {
    switch (status) {
      case 'completed':
        return <Chip label={t('drbfm.status.completed')} color="success" size="small" />;
      case 'in_progress':
        return <Chip label={t('drbfm.status.inProgress')} color="primary" size="small" />;
      case 'failed':
        return <Chip label={t('drbfm.status.failed')} color="error" size="small" />;
      default:
        return <Chip label={t('drbfm.status.pending')} variant="outlined" size="small" />;
    }
  };

  // Calculate overall progress percentage
  const progressPercent = Math.round(progress.overallProgress * 100);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('drbfm.processing.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('drbfm.processing.description')}
        </Typography>
      </Box>

      {/* Overall Progress */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1">
              {t('drbfm.processing.overallProgress')}
            </Typography>
            <Typography variant="h6" color="primary">
              {progressPercent}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ height: 10, borderRadius: 5 }}
          />
          {progress.error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {progress.error}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Flow Progress List */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            {t('drbfm.processing.flowStatus')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List disablePadding>
            {flowConfigs.map((flow, index) => {
              const status = progress.flowStatuses[flow.key];
              const isActive = progress.currentFlow === flow.id;

              return (
                <ListItem
                  key={flow.id}
                  sx={{
                    backgroundColor: isActive ? 'action.selected' : 'transparent',
                    borderRadius: 1,
                    mb: index < flowConfigs.length - 1 ? 1 : 0,
                  }}
                >
                  <ListItemIcon>
                    {getStatusIcon(status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {flow.icon}
                        <Typography variant="subtitle2">
                          Flow {flow.id}: {t(flow.labelKey)}
                        </Typography>
                      </Box>
                    }
                    secondary={t(flow.descriptionKey)}
                  />
                  {getStatusChip(status)}
                </ListItem>
              );
            })}
          </List>
        </CardContent>
      </Card>

      {/* Progress Messages */}
      {progress.messages.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {t('drbfm.processing.progressLog')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Paper
              variant="outlined"
              sx={{
                maxHeight: 200,
                overflow: 'auto',
                p: 2,
                backgroundColor: 'grey.50',
              }}
            >
              {progress.messages.map((message, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    mb: 0.5,
                  }}
                >
                  {message}
                </Typography>
              ))}
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* Cancel Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={!progress.isRunning}
        >
          {t('drbfm.processing.cancel')}
        </Button>
      </Box>

      {/* CSS for spinning animation */}
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default DRBFMProcessingStep;
